import CryptoJS from "crypto-js";
import moment from 'moment';
import { paymentConfig } from '../ecosystem.config';
import { TIPO_DOC } from './constants';

export * from './constants';

/*OBJECTS*/
export const isEmptyObject = (obj) => {
  return Object.keys(obj).length === 0;
}

/*NUMBERS*/
export const numberWithDots = (evt) => {
  return (evt ? evt.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".") : '0').toLocaleString('de-DE');
}

export const numberWithoutDots = (evt) => {
  return evt ? evt.toString().replace(".", "").replace(",", "") : '0';
}

export const numberBetween = (number, min, max) => {
  return number >= min && number <= max;
}

export const containsDoc = (str, doc) => str.includes(doc);
export const containsLowercase = str => /[a-z]/.test(str);
export const containsUppercase = str => /[A-Z]/.test(str);
export const containsNumber = str => /\d/.test(str);
export const containsLetter = str => /[A-Za-z]/.test(str);
export const containsNumberLetter = str => containsNumber(str) && containsLetter(str);
export const validLicensePlate = (license) => { return /^[A-Z]{3} ?\d{4}$/.test(license); }

/*EMAILS*/
export const validateEmail = (email) => {
  var re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

/* PASSWORD */
export const encodePassword = password => Buffer.from(password).toString('base64');

export const validatePassword = (psw, ci = '', rut = '', nroAsegurado = '') => (
  psw.length >= 8
  && containsUppercase(psw)
  && containsLowercase(psw)
  && containsNumberLetter(psw)
  && (!ci || !containsDoc(psw, ci))
  && (!rut || !containsDoc(psw, rut))
  && (!nroAsegurado || !containsDoc(psw, nroAsegurado))
);

/* VALIDACION DE CI Y RUT */
const ciValidationDigit = (ciParam: string) => {
  let ci = ciParam;
  ci = ci.substr(0, ci.length - 1);

  let suma = 0;
  let i = 0;
  const factores = [2, 9, 8, 7, 6, 3, 4];
  if (ci.length <= 6) {
    for (i = ci.length; i < 7; i++) {
      ci = '0' + ci;
    }
  }
  for (i = 0; i < 7; i++) {
    suma += (factores[i] * parseInt(ci[i], 10)) % 10;
  }
  if (suma % 10 === 0) {
    return 0 + '';
  }
  return (10 - (suma % 10)) + '';
};

const rutValidationDigit = (rutParam: string) => {
  let rut = rutParam;
  if (rut.length === 11) {
    rut = '0' + rut;
  }
  let dig;
  let suma = 0;
  let modulo = 0;
  const factores = [4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  for (let i = rut.length - 2; i >= 0; i--) {
    suma += parseInt(rut[i], 10) * factores[i];
  }
  modulo = suma % 11;
  dig = 11 - modulo;
  if (dig === 11) {
    dig = 0;
  }
  if (dig === 10) {
    dig = 1;
  }

  return dig + '';
};

export const validateCI = (val: string): boolean => {
  // Validación de CI

  // Obtener solo los número sin puntos ni guiones
  const doc = val.replace(/\D/g, '');
  // Obtengo el digito verificador del doc que me pasan
  const dig = doc[doc.length - 1];
  // Valido el largo del documento y comparo el digito verificador con el calculado
  const ciValid = doc.length <= 8 && dig === ciValidationDigit(doc);
  let containsOtherThanZero = false;
  for (let i = 0; i < doc.length; i++) {
    if (doc[i] !== '0') {
      containsOtherThanZero = true;
      break;
    }
  }
  if (!ciValid || !containsOtherThanZero) {
    return false; // "El formato de la CI no es válido"
  }
  return true;
};

export const validateRUT = (val) => {
  // Validación de RUT

  // Obtener solo los número sin puntos ni guiones
  const doc = val.replace(/\D/g, '');
  // Idem que en ci, solo cambia el algoritmo al que llamo y el largo
  const dig = doc[doc.length - 1];
  const rutValid = doc.length <= 12 && dig === rutValidationDigit(doc);
  let containsOtherThanZero = false;
  for (let i = 0; i < doc.length; i++) {
    if (doc[i] !== '0') {
      containsOtherThanZero = true;
      break;
    }
  }
  if (!rutValid || !containsOtherThanZero) {
    return false; // "El formato del RUT no es válido"
  }
  return true;
};

/*STRINGS*/
export const validateName = (name) => {
  var nameSplitted = name.split(' ');
  return nameSplitted.length>1;
}

export const toHexString = (byteArray) => {
  return Array.from(byteArray, function hex(byte) {
    return ('0' + (byte & 0xFF).toString(16)).slice(-2);
  }).join('')
}

export const justNumbers = str => {
  return str === '' || (str.match(/[^0-9]+/) ? false : true);
};

/*DATES*/
export const validateDate = (date, format = 'DD/MM/YYYY') => {
  return moment(date, format, true).isValid();
}

//dateString format dd/mm/YYYY
export const stringToDate = (dateString) => {
  var date = dateString.split("/");
  if(date.length === 3)
    return new Date(date[2], date[1] - 1, date[0]);
  if(date.length === 2)
    return new Date(date[1], date[0] - 1, 1);
  return new Date();
}

export const dateToString = (date) => {
  return date.getDate() + "/" + date.getMonth() + 1 + "/" + date.getFullYear();
}

export const compareDateWithToday = (date) => {
  const today = new Date();
  const toCompare = stringToDate(date);

  const year = toCompare.getFullYear() - today.getFullYear();
  const month = toCompare.getMonth() - today.getMonth();
  const day = toCompare.getDay() - today.getDay();

  //toCompare < today
  if(year < 0 || (year == 0 && month < 0) || (year == 0 && month == 0 && day < 0)) return -1;
  //toCompare == today
  if(year == 0 && month == 0 && day == 0) return 0;
  //toCompare > today
  return 1;
}

export const getAge = (birthdate) => {
  var today = new Date();
  var date = stringToDate(birthdate);
  var age = today.getFullYear() - date.getFullYear();
  var m = today.getMonth() - date.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < date.getDate())) {
      age--;
  }
  return age;
}

export const ageBetween = (age, min, max) => {
  return age <= max && age >= min;
}

/*ARRAYS*/
export const arrayToSelectList = (array) => {
  return array ? array.map(function func(obj) {
    var rObj = {};
    rObj['value'] = obj + '';
    rObj['label'] = obj + '';
    return rObj;
  }) : null;
}

export const mapSelectList = (list, value = 'clave', label = 'valor') => {
  return list ? list.map(function func(obj) {
    var rObj = {};
    rObj['value'] = obj[value];
    rObj['label'] = obj[label];
    return rObj;
  }) : null;
}

export const compareValues = (key, order = 'asc') => {
  return function innerSort(a, b) {
    // eslint-disable-next-line no-prototype-builtins
    if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
      return 0;
    }

    const varA = (typeof a[key] === 'string')
      ? a[key].toUpperCase() : a[key];
    const varB = (typeof b[key] === 'string')
      ? b[key].toUpperCase() : b[key];

    let comparison = 0;
    if (varA > varB) {
      comparison = 1;
    } else if (varA < varB) {
      comparison = -1;
    }
    return (
      (order === 'desc') ? (comparison * -1) : comparison
    );
  };
}

/*OTHERS*/
export const payments = (rate) => {
  var payments = [];

  rate.forEach(function func(obj) {
    if (obj.FeeAmount && obj.FeeAmount != 0) {
      payments.unshift({ value: obj.FeeAmount, label: obj.FeeAmount + ' cuotas' });
    }
  });

  return payments;
}

/* Ciudades de Canelones deshabilitadas */
export const disabledCities = [
  30, 101, 151, 813, 816, 817, 818, 819, 820, 821, 822, 823, 824,
  825, 826, 827, 828, 829, 830, 831, 832, 833, 834, 835, 840, 841
];

/* Códigos para Ciudades */
export const cityCodes = [
    {label: 'MONTEVIDEO',  code: 1},   {label: 'ARTIGAS',     code: 11},
    {label: 'CERRO LARGO', code: 131}, {label: 'COLONIA',     code: 153},
    {label: 'DURAZNO',     code: 211}, {label: 'FLORES',      code: 241},
    {label: 'FLORIDA',     code: 271}, {label: 'LAVALLEJA',   code: 301},
    {label: 'MALDONADO',   code: 331}, {label: 'PAYSANDU',    code: 371},
    {label: 'RIO NEGRO',   code: 421}, {label: 'RIVERA',      code: 461},
    {label: 'ROCHA',       code: 481}, {label: 'SALTO',       code: 521},
    {label: 'SAN JOSE',    code: 551}, {label: 'SORIANO',     code: 581},
    {label: 'TACUAREMBO',  code: 621}, {label: 'TREINTA Y TRES', code: 65}
];

export const getCardId = (paymentType) => {
  let cardId;
  switch (paymentType) {
    case 'master': cardId = 1; break;
    case 'visa':   cardId = 2; break;
    case 'oca':    cardId = 4; break;
    case 'red':    cardId = 0; break;
    case 'credit': cardId = 2; break;
    default:       cardId = null; break;
  }
  return cardId;
};

export const yearsRange = (startYear, range, order) => {
	var years = [];
  var lastYear = startYear + (range * order);
  while (startYear != lastYear) {
    years.push(startYear);
    startYear = startYear + order;
  }
  return years;
}

export const isValidFile = (type, size) => {
  /*let types = [
    'image/jpg',
    'image/jpeg',
    'image/png',
    'application/pdf',
    'application/msword',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ];*/

  let types = [
    'image/jpg',
    'image/jpeg'
  ];

  /* 5MB maximo */
  if (size > (10485760 / 2)) {
    return false;
  }

  if (!types.includes(type)) {
    return false;
  }

  return true;
};

export const imageSize = (base64String) => {
  let padding, inBytes, base64StringLength;
  if(base64String.endsWith("==")) padding = 2;
  else if (base64String.endsWith("=")) padding = 1;
  else padding = 0;

  base64StringLength = base64String.length;
  inBytes = (base64StringLength / 4 ) * 3 - padding;
  //console.log("inBytes::", inBytes);
  return inBytes;
};

export const imgType = (type) => {
  if (type.indexOf("jpeg") !== -1){
    return "JPEG";
  } else {
    return "JPG";
  }
}

/*HOME*/
  
//"type":"RECEIVE_FIRES"
export const fireOptions = [
  {Code: '4', Name: '5 DORMITORIOS',   Value: 125000},
  {Code: '3', Name: '4 DORMITORIOS',   Value: 100000},
  {Code: '2', Name: '3 DORMITORIOS',   Value: 75000},
  {Code: '1', Name: '1/2 DORMITORIOS', Value: 60000},
];
  
//"type":"RECEIVE_THEFTS"
export const theftOptions = [
  {Code: '6', Name: 'HURTO 500',   Value: 500,   ContentFire: 10000},
  {Code: '1', Name: 'HURTO 2000',  Value: 2000,  ContentFire: 10000},
  {Code: '2', Name: 'HURTO 4000',  Value: 4000,  ContentFire: 15000},
  {Code: '3', Name: 'HURTO 6000',  Value: 6000,  ContentFire: 25000},
  {Code: '4', Name: 'HURTO 8000',  Value: 8000,  ContentFire: 27500},
  {Code: '5', Name: 'HURTO 10000', Value: 10000, ContentFire: 30000},
];